import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Cursor, CursorTarget} from '@wonderlandengine/components';
import {TextFade} from '../components/text-fade.js';
import {SoundManagerInstance, Sounds} from './soundmanager.js';
import {quat} from 'gl-matrix';
import {Easing, clamp, lerpArr} from '../utils/lerp.js';
import {GameManagerInstance} from './gamemanager.js';
import {GA} from '../utils/ga-utils.js';
import {TextBubbles} from '../components/text-bubbles.js';
import {CursorReticle, CursorType} from '../components/cursor-reticle.js';

const tempQuat = quat.create();

/**
 * Call to action at the end of the game
 *
 * Script:
 * 1) Fade in
 * 2) Show Text
 * 3) Wait 5 seconds and door opens a bit
 * 4) When user clicks on door, Fade to white
 * 5) go to store on Quest / Steam on desktop
 */
export class CallToActionManager extends Component {
    static TypeName = 'call-to-action-manager';

    @property.object({required: true})
    fadeInObject!: Object3D;

    @property.object({required: true})
    fadeToWhiteObject!: Object3D;

    @property.object({required: true})
    doorObject!: Object3D;

    @property.object({required: true})
    endRotationObject!: Object3D;

    @property.object({required: true})
    textBubbleObject!: Object3D;

    @property.object({required: true})
    playerObject!: Object3D;

    private cursorTarget?: CursorTarget;
    private fadeToWhite?: TextFade;
    private doorOpening = false;
    private startRotation = quat.create();
    private endRotation = quat.create();
    private deltaRotation = 0;
    private fadeFromBlack?: TextFade;
    private textBubble?: TextBubbles;
    private cursorReticle?: CursorReticle;
    private doorInteraction = false;

    start(): void {
        const tempCursorTarget = this.doorObject.getComponent(CursorTarget);
        if (!tempCursorTarget) {
            throw new Error(
                `call-to-action-manager(${this.object.name}): doorObject does not have a CursorTarget`
            );
        }
        this.cursorTarget = tempCursorTarget;

        const tempTextFade = this.fadeToWhiteObject.getComponent(TextFade);
        if (!tempTextFade) {
            throw new Error(
                `call-to-action-manager(${this.object.name}): FadeToWhiteObject does not have a TextFade`
            );
        }
        this.fadeToWhite = tempTextFade;

        const tempTextFadeBlack = this.fadeInObject.getComponent(TextFade);
        if (!tempTextFadeBlack) {
            throw new Error(
                `call-to-action-manager(${this.object.name}): FadeInObject does not have a TextFadetempTextFade`
            );
        }
        this.fadeFromBlack = tempTextFadeBlack;

        const tempTextBubble = this.textBubbleObject.getComponent(TextBubbles);
        if (!tempTextBubble) {
            throw new Error(
                `call-to-action-manager(${this.object.name}): textBubbleObject does not have a TextBubble`
            );
        }
        this.textBubble = tempTextBubble;

        const tempCursorReticle = this.playerObject.getComponent(CursorReticle);
        if (!tempCursorReticle) {
            throw new Error(
                `call-to-action-manager(${this.object.name}): Player does not have a CursorReticle`
            );
        }
        this.cursorReticle = tempCursorReticle;

        this.doorObject.getRotationWorld(this.startRotation);
        this.endRotationObject.getRotationWorld(this.endRotation);

        setTimeout(() => this.fadeFromBlack!.fadeOut(), 1000);
        setTimeout(() => {
            this.doorOpening = true;
            SoundManagerInstance.playSound(Sounds.cupboardOpen);
        }, 12000);
    }

    onActivate(): void {
        this.cursorTarget!.onDown.add(this.openStore);
        this.cursorTarget!.onHover.add(this.onHoverDoor);
        this.cursorTarget!.onUnhover.add(this.onUnhoverDoor);
        this.fadeFromBlack!.fadedOut.add(this.showText);
    }

    onDeactivate(): void {
        this.cursorTarget!.onDown.remove(this.openStore);
        this.cursorTarget!.onHover.remove(this.onHoverDoor);
        this.cursorTarget!.onUnhover.remove(this.onUnhoverDoor);
        this.fadeFromBlack!.fadedOut.remove(this.showText);
    }

    update(delta: number): void {
        if (this.doorOpening) {
            this.deltaRotation += delta;
            this.deltaRotation = clamp(this.deltaRotation, 0, 1);
            if (!this.doorInteraction && this.deltaRotation > 0.35) {
                this.doorInteraction = true;
                this.doorOpening = false;
            }
            lerpArr(
                tempQuat,
                this.startRotation,
                this.endRotation,
                this.deltaRotation,
                Easing.InOutCubic
            );
            this.doorObject.setRotationWorld(tempQuat);
        }
    }

    showText = () => {
        this.textBubble!.onDown(undefined, undefined);
    };

    onHoverDoor = (_: Object3D, cursor: Cursor) => {
        this.cursorReticle!.changeCursorTexture(cursor, CursorType.Door);
    };

    onUnhoverDoor = (_: Object3D, cursor: Cursor) => {
        this.cursorReticle!.changeCursorTexture(cursor, CursorType.Default);
    };

    openStore = () => {
        this.doorOpening = true;
        this.doorInteraction = true;

        SoundManagerInstance.playSound(Sounds.doorOpen);
        this.fadeToWhite!.fadeSpeed = 1;
        this.fadeToWhite!.fadedIn.add(() => {
            this.doorOpening = false;
            if (this.engine.xr) {
                GA.sendConversion('exit_to_quest_store');
                // Assume we are on Quest
                this.engine.xr.session
                    .end()
                    .then(() => {
                        window.open(
                            'https://vr.meta.me/s/1P3ww95tiqkt5sO',
                            '_blank'
                        );
                    })
                    .catch(console.error);
            } else {
                GA.sendConversion('exit_to_steam');
                window.open(
                    'https://store.steampowered.com/app/671920/Dead_Secret_Circle/?utm_source=heyvr&utm_campaign=web_demo&utm_medium=demo',
                    '_blank'
                );
            }
            GameManagerInstance.stopAmbient();
        });

        this.fadeToWhite!.fadeIn();
    };
}
